/**
 * The facilities reconciliation report, in the facility table (AUTHORITY
 * RULING, Isaiah Cotto, 2026-10-05, round 2: facilities ship with native
 * editing, the ownership switch, Monday-write suppression, audit history AND a
 * reconciliation report). Read-only observations: eMerge is the authority for
 * every building, and nothing here changes one by itself. A building that
 * arrived from Monday is adopted by editing it, right here; a Monday item
 * eMerge does not hold is added here ("Add in eMerge", the item id as
 * evidence), or linked to the building eMerge already has -- either way the
 * item is then held and leaves the list.
 *
 * A hand-built drawer (no Radix Dialog; pages/admin/bd/bdUi.tsx). Shown to the
 * people who may manage buildings (facility.manage at enterprise scope, as
 * GET /scheduling/buildings/meta says). A section the Monday mirror cannot
 * answer says why; a failure is said, never an empty list.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer } from '../admin/bd/bdUi';
import { outcomeOf } from '../finance/financeApi';
import { AddBuildingForm, EditBuildingSection } from './BuildingEditor';
import { buildingsApi, BuildingMeta, ReconReport, ReconRow, ReconSection, ReconSectionKey } from './buildingsApi';

const NOT_RECORDED = 'not recorded';
const show = (v: string | string[] | null | undefined) => (v === null || v === undefined || (Array.isArray(v) && !v.length) || v === '' ? NOT_RECORDED : Array.isArray(v) ? v.join('; ') : v);
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : NOT_RECORDED);

function Row({ section, row, meta, onChanged }: { section: ReconSectionKey; row: ReconRow; meta: BuildingMeta; onChanged: (msg: string) => Promise<void> }) {
  const [adopting, setAdopting] = useState(false);
  const [adding, setAdding] = useState(false);
  if (section === 'not-in-emerge') {
    const item = row.monday_item_id || '';
    return (
      <li className="sc-recon-row">
        <strong>{show(row.name)}</strong> · {row.board} · Monday item {show(row.monday_item_id)}
        <span className="sc-src">On Monday since {day(row.first_seen_at)}. Add it in eMerge if it is a real building, with this Monday item id as evidence; if eMerge already has it, the duplicate check offers to link the item to that building.</span>
        {meta.can.create && item && (
          adding
            ? (
              <AddBuildingForm
                meta={meta} initial={{ name: row.name || '', monday_item_id: item }} onCancel={() => setAdding(false)}
                onDone={async (b, how) => { setAdding(false); await onChanged(how === 'linked' ? `Monday item ${item} linked to ${b.name}.` : `${b.name}: added in eMerge, linked to Monday item ${item}.`); }}
              />
            )
            : <div className="sc-cmds"><button type="button" className="um-open" onClick={() => setAdding(true)} aria-label={`Add ${show(row.name)} in eMerge, with Monday item ${item} as evidence`}>Add in eMerge</button></div>
        )}
      </li>
    );
  }
  return (
    <li className="sc-recon-row">
      <strong>{show(row.name)}</strong>{row.monday_item_id ? ` · ${row.linked ? 'linked ' : ''}Monday item ${row.monday_item_id}` : ''}{row.board ? ` · ${row.board}` : ''}
      {section === 'awaiting-adoption' && <span className="sc-src">Arrived {day(row.arrived_at)}; not yet adopted.</span>}
      {section === 'gone-from-monday' && <span className="sc-src">{row.what}. The building stays in eMerge.</span>}
      {section === 'differs' && row.differences && (
        <ul className="sc-recon-diff" aria-label={`${show(row.name)}: where Monday differs`}>
          {row.differences.map((d) => (
            <li key={d.field}>{d.label}: eMerge <strong>{show(d.emerge)}</strong> · Monday {show(d.monday)}</li>
          ))}
        </ul>
      )}
      {section === 'awaiting-adoption' && row.facility_id && meta.can.edit && (
        adopting
          ? <EditBuildingSection facilityId={row.facility_id} name={show(row.name)} meta={meta} onChanged={async (m) => { setAdopting(false); await onChanged(m); }} />
          : <div className="sc-cmds"><button type="button" className="um-open" onClick={() => setAdopting(true)} aria-label={`Adopt ${show(row.name)}: open its details to edit`}>Adopt: edit its details</button></div>
      )}
    </li>
  );
}

/** The report itself: in the drawer below, and as the body of the Buildings page (ruling R5, pages/scheduling/BuildingsWorkspace.tsx). */
export function ReconciliationReport({ meta, onNotice }: { meta: BuildingMeta; onNotice: (msg: string) => void }) {
  const [section, setSection] = useState<ReconSectionKey>('awaiting-adoption');
  const [page, setPage] = useState(1);
  const [report, setReport] = useState<ReconReport | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The section chips outlive a reload, so the chip just pressed keeps focus while the next section loads.
  const [sections, setSections] = useState<ReconSection[]>([]);

  // What is selected now, kept synchronously (set where the selection changes, before React re-renders). A load
  // belongs to the section and page it was made for: one made for a selection the reader has left -- a slower
  // answer, or an add or edit begun there that finishes later and reloads through its old callback -- neither
  // applies its rows nor takes a number, so it cannot invalidate the current selection's load (Copilot 4266936215).
  const selRef = useRef<{ section: ReconSectionKey; page: number }>({ section, page });
  const choose = (nextSection: ReconSectionKey, nextPage: number) => {
    selRef.current = { section: nextSection, page: nextPage };
    setReport(null); setSection(nextSection); setPage(nextPage);
  };
  // Every load for the current selection gets a number; only the latest one's answer is applied (as RevetsDue does).
  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const isSelected = () => selRef.current.section === section && selRef.current.page === page;
    if (!isSelected()) return;
    const seq = ++seqRef.current;
    const latest = () => seq === seqRef.current && isSelected();
    setErr(null);
    try {
      const r = await buildingsApi.reconciliation(section, page);
      if (!latest()) return;
      if (r.success && r.data) { setReport(r.data); setSections(r.data.sections); } else { setReport(null); setErr(outcomeOf(r)); }
    } catch { if (latest()) { setReport(null); setErr('Could not reach the server.'); } }
  }, [section, page]);
  useEffect(() => { load(); }, [load]);

  const onChanged = async (msg: string) => { onNotice(msg); await load(); };
  const current = sections.find((s) => s.key === section) || null;

  return (
    <div style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div className="sc-chips" role="group" aria-label="Reconciliation sections">
        {sections.map((s) => (
          <button key={s.key} type="button" className="um-open" aria-pressed={section === s.key} title={s.what} onClick={() => { if (s.key !== section) choose(s.key, 1); }}>
            {s.label}<span className="n">{s.count === null ? 'n/a' : s.provisional ? `${s.count}+` : s.count}</span>
          </button>
        ))}
      </div>
      {err && <p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={load}>Try again</button></p>}
      {!err && !report && <p className="um-quiet" role="status">Loading the report…</p>}
      {report && current && <p className="um-quiet" style={{ margin: 0 }}>{current.what}</p>}
      {report && report.mirror.state !== 'READ' && section !== 'awaiting-adoption' && (
        <p className="um-notice" role="status">{report.mirror.reason}</p>
      )}
      {/* One board not mirrored yet: what is shown is the other board's, never the whole answer. */}
      {report && report.provisional && <p className="um-notice" role="status">Provisional: {report.provisional}</p>}
      {report && report.message && report.message !== report.provisional && !(report.mirror.state !== 'READ' && section !== 'awaiting-adoption') && <p className="um-quiet" role="status">{report.message}</p>}
      {report && report.rows.length > 0 && (
        <ul aria-label={current ? current.label : 'Rows'} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {/* Rendered as the section the rows came from, never the one just clicked. */}
          {report.rows.map((row) => <Row key={`${report.section}:${row.facility_id || ''}:${row.monday_item_id || ''}`} section={report.section} row={row} meta={meta} onChanged={onChanged} />)}
        </ul>
      )}
      {report && report.pages !== null && report.pages > 1 && (
        <div className="sc-pager">
          {/* Provisional: one board is not compared yet, so the total is a floor, as the chips say ("N+"). */}
          <span>Page {report.page} of {report.pages} · {report.provisional ? `${report.total}+ so far (provisional)` : `${report.total} in all`}</span>
          <div className="btns">
            <button type="button" className="um-open" disabled={page <= 1} onClick={() => choose(section, Math.max(1, page - 1))}>Previous</button>
            <button type="button" className="um-open" disabled={report.pages === null || page >= report.pages} onClick={() => choose(section, page + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** "Monday reconciliation", for the people who may manage buildings. */
export function ReconciliationButton({ meta, onNotice }: { meta: BuildingMeta | null; onNotice: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [round, setRound] = useState(0);
  if (!meta || !meta.enterprise) return null;
  return (
    <>
      <button type="button" className="board-btn um-open" onClick={() => { setRound((n) => n + 1); setOpen(true); }}>Monday reconciliation</button>
      <Drawer open={open} onClose={() => setOpen(false)} title="Monday reconciliation" description="Where Monday and eMerge disagree about a building. eMerge is the authority; nothing here changes Monday or a building by itself.">
        <ReconciliationReport key={round} meta={meta} onNotice={onNotice} />
      </Drawer>
    </>
  );
}
